/**
 * D-EZ-95 — EVERYONE WHO EVER RACED IN THE LEAGUE.
 *
 * The record book used to know a driver only while he was on the current
 * season's roster: retire him and the money table printed his raw id
 * ("drv-s3-07"), and his dossier would not open. The registry is the league's
 * memory of every licensed driver — name, number, last ride, how his career
 * ended, and his career line — folded from every archived season plus the one
 * being raced.
 */

import { ageOf } from '../aging.ts';
import type { CareerPrior } from './types.ts';
import { earningsBy, sortedSeasons, winningsBy, type HistorySeason } from './source.ts';

export type CareerStatus = 'active' | 'retired' | 'licence-lost' | 'died' | 'left';

/** One season of a driver's career, as the record book prints it. */
export interface SeasonLine {
  seasonNumber: number;
  teamId: string | null;
  teamName: string | null;
  carNumber: string | null;
  /** Championship rank (D-EZ-90 order), and rank on points alone. */
  rank: number;
  pointsRank: number;
  points: number;
  starts: number;
  wins: number;
  poles: number;
  top5: number;
  top10: number;
  dnfs: number;
  turnsLed: number;
  winningsMb: number;
  /** What his own book netted — salary, winnings, royalties, bonus, less fines. */
  earningsMb: number;
  champion: boolean;
  /** False for the season still being raced. */
  complete: boolean;
}

export interface CareerTotals {
  seasons: number;
  starts: number;
  wins: number;
  poles: number;
  top5: number;
  top10: number;
  dnfs: number;
  turnsLed: number;
  points: number;
  championships: number;
  winningsMb: number;
  earningsMb: number;
}

export interface RegistryEntry {
  driverId: string;
  name: string;
  carNumber: string | null;
  /** The last team he drove for, and its name then. */
  teamId: string | null;
  teamName: string | null;
  firstSeason: number;
  lastSeason: number;
  status: CareerStatus;
  /** His age in his last (or current) season, when known. */
  age: number | null;
  /** In-league career only. */
  league: CareerTotals;
  /** Pre-league career, where the scenario supplied one (D-EZ-97). */
  prior: CareerPrior | null;
  /** League + prior, field by field (a prior field that is unknown adds 0). */
  career: CareerTotals;
  bySeason: SeasonLine[];
  /** His best season by championship rank (ties: more points). */
  bestSeason: SeasonLine | null;
}

export interface DriverRegistry {
  byId: Record<string, RegistryEntry>;
  /** Everyone, most career wins first. */
  all: RegistryEntry[];
  active: RegistryEntry[];
  /** Everyone no longer licensed, most recent departure first. */
  former: RegistryEntry[];
}

function zero(): CareerTotals {
  return {
    seasons: 0,
    starts: 0,
    wins: 0,
    poles: 0,
    top5: 0,
    top10: 0,
    dnfs: 0,
    turnsLed: 0,
    points: 0,
    championships: 0,
    winningsMb: 0,
    earningsMb: 0,
  };
}

function addPrior(t: CareerTotals, p: CareerPrior | null): CareerTotals {
  if (!p) return { ...t };
  return {
    seasons: t.seasons + (p.seasons ?? 0),
    starts: t.starts + (p.starts ?? 0),
    wins: t.wins + (p.wins ?? 0),
    poles: t.poles + (p.poles ?? 0),
    top5: t.top5 + (p.top5 ?? 0),
    top10: t.top10 + (p.top10 ?? 0),
    dnfs: t.dnfs,
    turnsLed: t.turnsLed,
    points: t.points,
    championships: t.championships + (p.championships ?? 0),
    winningsMb: t.winningsMb + (p.earningsMb ?? 0),
    earningsMb: t.earningsMb + (p.earningsMb ?? 0),
  };
}

/**
 * Build the registry. `priors` defaults to whatever the latest winter carried
 * (`offseason.carry.priors`); pass it explicitly for a live first season.
 */
export function buildRegistry(
  input: readonly HistorySeason[],
  opts: { priors?: Readonly<Record<string, CareerPrior>> } = {},
): DriverRegistry {
  const seasons = sortedSeasons(input);
  const priors: Readonly<Record<string, CareerPrior>> =
    opts.priors ??
    [...seasons].reverse().find((s) => s.offseason?.carry?.priors)?.offseason?.carry?.priors ??
    {};

  const entries = new Map<string, RegistryEntry>();
  const latest = seasons[seasons.length - 1];

  for (const s of seasons) {
    const teamName = new Map(s.teams.map((t) => [t.teamId, t.name]));
    const cardOf = new Map(s.roster.map((c) => [c.driverId, c]));
    const winnings = winningsBy(s.finance?.events);
    const earnings = earningsBy(s.finance?.events);
    const complete = s.complete !== false;

    // Everyone on the roster, plus anyone who scored without being on it (a
    // mid-season rookie), so nobody who raced is missing.
    const ids = new Set<string>([...s.roster.map((c) => c.driverId), ...s.season.finalStandings.drivers.map((d) => d.driverId)]);
    for (const id of ids) {
      const stat = s.season.finalStandings.byDriver[id];
      const card = cardOf.get(id);
      let e = entries.get(id);
      if (!e) {
        e = {
          driverId: id,
          name: card?.name ?? stat?.name ?? id,
          carNumber: card?.carNumber ?? null,
          teamId: null,
          teamName: null,
          firstSeason: s.seasonNumber,
          lastSeason: s.seasonNumber,
          status: 'active',
          age: null,
          league: zero(),
          prior: priors[id] ?? null,
          career: zero(),
          bySeason: [],
          bestSeason: null,
        };
        entries.set(id, e);
      }
      e.name = stat?.name ?? card?.name ?? e.name;
      e.carNumber = card?.carNumber ?? e.carNumber;
      const teamId = stat?.teamId ?? card?.teamId ?? null;
      e.teamId = teamId;
      e.teamName = teamId ? (teamName.get(teamId) ?? null) : null;
      e.lastSeason = s.seasonNumber;
      if (card) e.age = ageOf(card);
      if (!stat) continue;
      const line: SeasonLine = {
        seasonNumber: s.seasonNumber,
        teamId,
        teamName: e.teamName,
        carNumber: e.carNumber,
        rank: stat.rank,
        pointsRank: stat.pointsRank ?? stat.rank,
        points: stat.points,
        starts: stat.starts,
        wins: stat.wins,
        poles: stat.poles,
        top5: stat.top5,
        top10: stat.top10,
        dnfs: stat.dnfs,
        turnsLed: stat.turnsLed,
        winningsMb: winnings.get(id) ?? 0,
        earningsMb: earnings.get(id) ?? 0,
        champion: s.season.championId === id,
        complete,
      };
      e.bySeason.push(line);
      const t = e.league;
      if (stat.starts > 0) t.seasons += 1;
      t.starts += line.starts;
      t.wins += line.wins;
      t.poles += line.poles;
      t.top5 += line.top5;
      t.top10 += line.top10;
      t.dnfs += line.dnfs;
      t.turnsLed += line.turnsLed;
      t.points += line.points;
      t.winningsMb += line.winningsMb;
      t.earningsMb += line.earningsMb;
      if (line.champion) t.championships += 1;
    }

    // How careers ended this winter.
    if (s.offseason) {
      const next = new Set(s.offseason.roster.map((c) => c.driverId));
      const retired = new Map((s.offseason.retirements ?? []).map((r) => [r.driverId, r]));
      const lost = new Set(s.offseason.demotions.map((d) => d.driverId));
      for (const id of ids) {
        const e = entries.get(id);
        if (!e) continue;
        if (next.has(id)) {
          e.status = 'active';
          continue;
        }
        e.status = retired.has(id) ? 'retired' : lost.has(id) ? 'licence-lost' : 'left';
      }
    }
    // Deaths recorded by the season itself.
    for (const f of s.season.fatalities ?? []) {
      const e = entries.get(f.driverId);
      if (e) e.status = 'died';
    }
  }

  // Anyone not on the latest roster (or the latest winter's roster) is former.
  if (latest) {
    const current = new Set((latest.offseason?.roster ?? latest.roster).map((c) => c.driverId));
    for (const e of entries.values()) {
      if (current.has(e.driverId)) e.status = 'active';
      else if (e.status === 'active') e.status = 'left';
    }
  }

  for (const e of entries.values()) {
    e.career = addPrior(e.league, e.prior);
    const done = e.bySeason.filter((l) => l.starts > 0);
    e.bestSeason =
      done.length === 0
        ? null
        : done.reduce((best, l) => (l.rank < best.rank || (l.rank === best.rank && l.points > best.points) ? l : best));
  }

  const all = [...entries.values()].sort(
    (a, b) => b.career.wins - a.career.wins || b.career.championships - a.career.championships || b.career.starts - a.career.starts || a.driverId.localeCompare(b.driverId),
  );
  return {
    byId: Object.fromEntries(all.map((e) => [e.driverId, e])),
    all,
    active: all.filter((e) => e.status === 'active'),
    former: all
      .filter((e) => e.status !== 'active')
      .sort((a, b) => b.lastSeason - a.lastSeason || a.name.localeCompare(b.name)),
  };
}

/**
 * A driver's name from anywhere in the league's history — the fix for retired
 * drivers printing as raw ids. Falls back to the id only for an id the league
 * has never seen.
 */
export function registryName(registry: DriverRegistry, driverId: string): string {
  return registry.byId[driverId]?.name ?? driverId;
}

/** A name resolver over a set of seasons, for screens that only need names. */
export function nameResolver(seasons: readonly HistorySeason[]): (driverId: string) => string {
  const names = new Map<string, string>();
  for (const s of sortedSeasons(seasons)) {
    for (const c of s.roster) names.set(c.driverId, c.name);
    for (const d of s.season.finalStandings.drivers) names.set(d.driverId, d.name);
    for (const c of s.offseason?.roster ?? []) names.set(c.driverId, c.name);
  }
  return (id) => names.get(id) ?? id;
}
